import { useEffect } from 'react';
import { useController } from '@data-client/react';

import { DataClientController } from '../../../classes/data-client-controller';

/**
 * Mount this inside `<DataProvider>` at the app root, as its FIRST child. It
 * registers the Data Client controller into a module-level ref so imperative
 * callers (Api.write, Api.invalidate, etc.) can reach it outside React.
 *
 * Registration happens during render rather than in an effect, and the ordering
 * is load-bearing. `Api.read` consults `DataClientController.has()` while the
 * tree is still rendering to decide between the normalized Data Client cache
 * and the legacy `EndpointCache`. Effects run only after the whole tree
 * commits, so an effect-based registration is too late for the initial render
 * pass: every schema-bearing read in it silently falls back to the legacy
 * store. That entry then lives on forever alongside the Data Client one, and
 * `Api.invalidate` — which hits both stores — refetches through each of them,
 * putting two identical requests on the wire per invalidation.
 *
 * Writing to a module ref during render is a side effect, but an idempotent one
 * on a value React guarantees is stable, and children render after their parent
 * so the whole subtree sees a registered controller.
 */
export const DataClientProviderBridge = () => {
  const controller = useController();

  if (!DataClientController.is(controller)) {
    DataClientController.set(controller);
  }

  useEffect(() => {
    // Already set by the render above on the common path; this covers a
    // controller swap, where the previous effect's cleanup nulls the ref out
    // after the new controller was registered.
    if (!DataClientController.is(controller)) {
      DataClientController.set(controller);
    }
    return () => {
      DataClientController.set(null);
    };
  }, [controller]);

  return null;
};
